
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { ImageLibraryItem, SaveImageToLibraryParams, ImageLibraryFilter } from './types';
import { 
  fetchImageLibrary, 
  saveImageToLibrary, 
  deleteImageFromLibrary, 
  toggleFavoriteImage, 
  isImageFavorited,
  isImageFavoritedWithUserId,
} from './api';
import { supabase } from '@/integrations/supabase/client';

const DEFAULT_LIMIT = 12;

/**
 * Custom hook for managing image library operations
 */
export const useImageLibrary = () => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Query to fetch image library with pagination
  const useImageLibraryQuery = (filters?: ImageLibraryFilter) => {
    const infiniteQuery = useInfiniteQuery({
      queryKey: ['imageLibrary', filters],
      queryFn: async ({ pageParam = 0 }) => {
        const pageResult = await fetchImageLibrary({ 
          ...filters, 
          page: pageParam, 
          limit: filters?.limit || DEFAULT_LIMIT 
        });

        if (pageResult && pageResult.data) {
          // ✅ FIX: Get session ONCE per page, not per image
          const { data: { session } } = await supabase.auth.getSession();
          const userId = session?.user?.id;
          
          const enrichedData = await Promise.all(
            pageResult.data.map(async (item: ImageLibraryItem) => {
              try {
                // ✅ FIX: Pass cached session instead of calling getSession() again
                const isFav = userId ? await isImageFavoritedWithUserId(item.id, userId) : false;
                return { ...item, is_favorite: isFav };
              } catch (error) {
                console.warn(`[useImageLibrary] Failed to fetch favorite status for image ${item.id}:`, error);
                return { ...item, is_favorite: false }; // Default to false on error
              }
            })
          );
          return { ...pageResult, data: enrichedData };
        }
        return pageResult;
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.hasMore) {
          return lastPage.page + 1;
        }
        return undefined;
      },
      initialPageParam: 0
    });

    // Helper to get flattened data from all pages
    const flatData = infiniteQuery.data?.pages.flatMap(page => page.data) || [];
    
    // Helper function to fetch the next page
    const loadMore = useCallback(() => {
      if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
        infiniteQuery.fetchNextPage();
      }
    }, [infiniteQuery]);

    return {
      ...infiniteQuery,
      flatData,
      loadMore,
    };
  };

  // Save image to library
  const saveImageToLibraryMutation = async (params: SaveImageToLibraryParams) => {
    setIsLoading(true);
    try {
      const result = await saveImageToLibrary(params);
      
      // Invalidate query cache
      if (result) {
        queryClient.invalidateQueries({ queryKey: ['imageLibrary'] });
      }
      
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete image from library with optimistic updates
  const deleteImage = useMutation({
    mutationFn: deleteImageFromLibrary,
    onMutate: async (imageId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['imageLibrary'] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['imageLibrary'] });
      
      // Optimistically remove the image from all cached queries
      queryClient.setQueriesData({ queryKey: ['imageLibrary'] }, (oldData: any) => {
        if (!oldData) return oldData;
        
        // Update data in all pages
        const updatedPages = oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.filter((item: ImageLibraryItem) => item.id !== imageId)
        }));
        
        return {
          ...oldData,
          pages: updatedPages
        };
      });
      
      // Return a context object with the previous data for rollback
      return { previousData };
    },
    onError: (err, imageId, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ 
        queryKey: ['imageLibrary'],
        exact: false
      });
    }
  });

  // Toggle favorite status for an image with optimistic updates
  const toggleFavorite = useMutation({
    mutationFn: toggleFavoriteImage,
    // Use optimistic updates to prevent UI flashing
    onMutate: async (imageId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['imageLibrary'] });
      
      // Get current favorite status
      const previousFavoriteStatus = await isImageFavorited(imageId);
      
      // Optimistically update the UI
      queryClient.setQueriesData({ queryKey: ['imageLibrary'] }, (oldData: any) => {
        if (!oldData) return oldData;
        
        // Update data in all pages
        const updatedPages = oldData.pages.map((page: any) => {
          const updatedData = page.data.map((item: ImageLibraryItem) => {
            if (item.id === imageId) {
              // Toggle the favorite status
              const newIsFavorite = !previousFavoriteStatus;
              // Update favorite count accordingly
              const newFavoriteCount = newIsFavorite 
                ? (item.favorite_count || 0) + 1 
                : Math.max(0, (item.favorite_count || 0) - 1);
              
              return {
                ...item,
                is_favorite: newIsFavorite,
                favorite_count: newFavoriteCount
              };
            }
            return item;
          });
          
          return {
            ...page,
            data: updatedData
          };
        });
        
        return {
          ...oldData,
          pages: updatedPages
        };
      });
      
      // Return previous state for rollback
      return { previousFavoriteStatus, imageId };
    },
    onError: (err, imageId, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueriesData({ queryKey: ['imageLibrary'] }, (oldData: any) => {
          if (!oldData) return oldData;
          
          const updatedPages = oldData.pages.map((page: any) => {
            const updatedData = page.data.map((item: ImageLibraryItem) => {
              if (item.id === context.imageId) {
                // Revert to previous state
                const prevFavoriteCount = context.previousFavoriteStatus 
                  ? (item.favorite_count || 0) + 1 
                  : Math.max(0, (item.favorite_count || 0) - 1);
                
                return {
                  ...item,
                  is_favorite: context.previousFavoriteStatus,
                  favorite_count: prevFavoriteCount
                };
              }
              return item;
            });
            
            return {
              ...page,
              data: updatedData
            };
          });
          
          return {
            ...oldData,
            pages: updatedPages
          };
        });
      }
    },
    onSettled: (data, error, imageId) => {
      // Invalidate all imageLibrary queries to ensure all components get updated
      queryClient.invalidateQueries({ queryKey: ['imageLibrary'] });
    }
  });

  // Check if an image is favorited
  const checkFavoriteStatus = useCallback(async (imageId: string) => {
    return await isImageFavorited(imageId);
  }, []);

  return {
    useImageLibraryQuery,
    saveImageToLibrary: saveImageToLibraryMutation,
    deleteImage,
    toggleFavorite,
    checkFavoriteStatus,
    isLoading,
    searchTerm,
    setSearchTerm
  };
};
